/**
 * Canonical registry of the standalone-web happy-path journey (ENG-4330).
 *
 * The browser build has FOUR journey stages, not the desktop nine: there is no
 * bridge link, no permissions modal, no local discovery and no in-app download
 * on web, so those desktop stages have no web analogue and none is invented
 * here. This module names the four that do fire, maps each to the canonical
 * event that measures it, and states the envelope every one of them must carry
 * for `client`-split funnels to be readable.
 *
 * Both surfaces run the SAME emit sites; the only thing that differs is the
 * `client` value the context resolver stamps (`isElectronApp()` →
 * ENVELOPE_CLIENT.ELECTRON | ENVELOPE_CLIENT.STANDALONE_WEB). That single
 * discriminator is what a consumer splits on, which is why it is listed as a
 * required envelope key rather than left implicit.
 *
 * Per rule: think-in-systems — one canonical list, composed from EVENT_NAMES so
 * the registry can never drift to a stale string literal (same shape as the
 * sibling device-events.ts registry).
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */

import { COMMON_PROPERTIES, DEVICE_PROPERTIES } from './event-properties.js';
import { EVENT_NAMES } from './event-names.js';
import type { EventName } from './event-names.js';

/** The four journey stages that exist on the standalone-web build. */
export const WEB_FUNNEL_STAGE = {
  BOOT: 'boot',
  SIGN_IN: 'sign_in',
  ADD_CAMERA: 'add_camera',
  FIRST_STREAM: 'first_stream'
} as const;

export type WebFunnelStage = (typeof WEB_FUNNEL_STAGE)[keyof typeof WEB_FUNNEL_STAGE];

/**
 * Stage → the canonical event that marks the stage as reached.
 *
 * `satisfies` keeps the map total over WebFunnelStage (declaring a stage
 * without its event is a compile error) while every value stays literal-typed,
 * so a consumer reading `WEB_FUNNEL_STAGE_EVENTS.BOOT` still gets
 * `'boot_succeeded'` and not a widened `string`.
 *
 * BOOT resolves to `boot_succeeded` rather than `boot_started`: a funnel stage
 * is "reached", and a started-but-never-finished boot did not reach it.
 * FIRST_STREAM resolves to `media_stream_succeeded`. Its retired predecessor
 * remains in the historical event catalog, but new builds no longer emit it.
 */
export const WEB_FUNNEL_STAGE_EVENTS = {
  [WEB_FUNNEL_STAGE.BOOT]: EVENT_NAMES.BOOT_SUCCEEDED,
  [WEB_FUNNEL_STAGE.SIGN_IN]: EVENT_NAMES.LOGIN_SUCCEEDED,
  [WEB_FUNNEL_STAGE.ADD_CAMERA]: EVENT_NAMES.DEVICE_ADD,
  [WEB_FUNNEL_STAGE.FIRST_STREAM]: EVENT_NAMES.MEDIA_STREAM_SUCCEEDED
} as const satisfies Record<WebFunnelStage, EventName>;

/** The canonical event that measures a given web journey stage. */
export type WebFunnelStageEvent =
  (typeof WEB_FUNNEL_STAGE_EVENTS)[keyof typeof WEB_FUNNEL_STAGE_EVENTS];

/**
 * Set form for O(1) "is this event part of the web funnel?" membership checks.
 *
 * Typed `ReadonlySet<string>` rather than `ReadonlySet<EventName>` so the guard
 * below can take an unvalidated name without a type assertion. The membership
 * VALUES are still `WebFunnelStageEvent` by construction (`satisfies` above),
 * so nothing is lost — only the lookup argument is widened.
 */
export const WEB_FUNNEL_EVENTS: ReadonlySet<string> = new Set<string>(
  Object.values(WEB_FUNNEL_STAGE_EVENTS)
);

/**
 * Narrowing membership check. Takes `string`, not `EventName`, because callers
 * hold a name that has NOT been validated against the canonical union —
 * `AnalyticsEvent['name']` also admits the legacy dual-write twins. Returning a
 * type predicate is what lets those call sites narrow instead of asserting.
 */
export const isWebFunnelEvent = (name: string): name is WebFunnelStageEvent =>
  WEB_FUNNEL_EVENTS.has(name);

/**
 * Envelope keys a web-funnel event MUST carry to be usable in a client-split
 * funnel. `client` is the surface discriminator itself; `launch_id` +
 * `session_id` are what let a consumer collapse one browser tab's stages into
 * one journey; `env` / `vendor` / `platform` / `app_version` scope the cohort.
 *
 * The context resolver already stamps all of these on every named event — this
 * list exists so a regression that drops one is caught by the dev-time guard in
 * track.ts and by the CI receipts, instead of surfacing weeks later as a funnel
 * that silently lost its web half.
 */
export const WEB_FUNNEL_REQUIRED_ENVELOPE = [
  COMMON_PROPERTIES.CLIENT,
  COMMON_PROPERTIES.ENV,
  COMMON_PROPERTIES.VENDOR,
  COMMON_PROPERTIES.PLATFORM,
  COMMON_PROPERTIES.APP_VERSION,
  COMMON_PROPERTIES.LAUNCH_ID,
  COMMON_PROPERTIES.SESSION_ID_CANONICAL
] as const;

export type WebFunnelEnvelopeKey = (typeof WEB_FUNNEL_REQUIRED_ENVELOPE)[number];

/**
 * The identity a consumer must collapse on to count a stage ONCE.
 *
 * A raw event count is not the stage count. Historical data contains both names
 * for the same first stream, and the retired series also contains unmatched
 * reconnects. These keys let readers collapse only a proven old/new duplicate
 * while keeping every unmatched row.
 */
export const WEB_FUNNEL_STAGE_IDENTITY = {
  [WEB_FUNNEL_STAGE.BOOT]: [COMMON_PROPERTIES.LAUNCH_ID],
  [WEB_FUNNEL_STAGE.SIGN_IN]: [COMMON_PROPERTIES.LAUNCH_ID],
  [WEB_FUNNEL_STAGE.ADD_CAMERA]: [
    COMMON_PROPERTIES.LAUNCH_ID,
    DEVICE_PROPERTIES.DEVICE_ID_CANONICAL
  ],
  [WEB_FUNNEL_STAGE.FIRST_STREAM]: [
    COMMON_PROPERTIES.LAUNCH_ID,
    DEVICE_PROPERTIES.DEVICE_ID_CANONICAL
  ]
} as const satisfies Record<WebFunnelStage, readonly string[]>;

/**
 * Widened view of one stage's identity keys. The `as const` map above gives each
 * stage its own tuple type, and calling an array method on that union of tuples
 * has no single applicable signature — so read through this accessor, whose
 * declared return type does the widening without a type assertion.
 */
const identityKeysFor = (stage: WebFunnelStage): readonly string[] =>
  WEB_FUNNEL_STAGE_IDENTITY[stage];

/** `false` and `0` count as PRESENT — see {@link missingWebFunnelEnvelopeKeys}. */
const isAbsent = (value: unknown): boolean => value === undefined || value === null || value === '';

/**
 * Identity keys missing (or empty) on an already-enriched stage event. Mirrors
 * {@link missingWebFunnelEnvelopeKeys}: a stage whose identity is incomplete
 * cannot be de-duplicated, so a double emit silently inflates it.
 */
export const missingWebFunnelStageIdentityKeys = (
  stage: WebFunnelStage,
  properties: Readonly<Record<string, unknown>>
): string[] => identityKeysFor(stage).filter((key) => isAbsent(properties[key]));

/**
 * The value a consumer groups by to count {@link stage} once. Returns undefined
 * when any identity key is absent — an event that cannot be attributed to an
 * identity must not silently collapse into a shared bucket.
 */
export const webFunnelStageIdentity = (
  stage: WebFunnelStage,
  properties: Readonly<Record<string, unknown>>
): string | undefined => {
  if (missingWebFunnelStageIdentityKeys(stage, properties).length > 0) {
    return undefined;
  }

  return identityKeysFor(stage)
    .map((key) => String(properties[key]))
    .join('|');
};

/**
 * Envelope keys missing (or empty) on an already-enriched web-funnel event.
 * Returns [] for an event that is not part of the funnel, so callers can run it
 * unconditionally. `false` and `0` count as PRESENT — telemetry values are
 * legitimately falsy and treating them as missing would raise false alarms.
 */
export const missingWebFunnelEnvelopeKeys = (
  name: string,
  properties: Readonly<Record<string, unknown>>
): WebFunnelEnvelopeKey[] => {
  if (!isWebFunnelEvent(name)) {
    return [];
  }

  return WEB_FUNNEL_REQUIRED_ENVELOPE.filter((key) => isAbsent(properties[key]));
};
